import { useEffect, useMemo, useState } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

const API_URL = 'http://groupe-2ms.com /api';

interface Invoice {
  id: string;
  numero: string;
  bon_livraison?: { numero: string };
  client: { raison_sociale: string; client_id: number };
  total_ttc: number;
  montant_paye: number;
  date_echeance: string;
  statut_paiement: string;
}

export default function DueDatesBoard() {
  const [overdueInvoices, setOverdueInvoices] = useState<Invoice[]>([]);
  const [upcomingInvoices, setUpcomingInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  // États pour le modal de règlement
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('VIREMENT');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [referenceExterne, setReferenceExterne] = useState('');
  const [dateValeur, setDateValeur] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  /* ================= FETCH ================= */
  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      try {
        const res = await getResources(`factures-client`);
        const rawInvoices = res.data.data || [];

        const invoices: Invoice[] = rawInvoices.map((f: any) => ({
          id: String(f.id),
          numero: f.numero,
          bon_livraison: f.bon_livraison,
          client: f.client,
          total_ttc: Number(f.total_ttc),
          montant_paye: Number(f.montant_paye || 0),
          date_echeance: f.date_echeance,
          statut_paiement: f.statut_paiement,
        }));

        const today = new Date();

        const overdue = invoices.filter(inv => {
          const due = new Date(inv.date_echeance);
          const balance = inv.total_ttc - inv.montant_paye;
          return due < today && balance > 0;
        });

        const upcoming = invoices.filter(inv => {
          const due = new Date(inv.date_echeance);
          const daysUntil = Math.ceil((due.getTime() - today.getTime()) / (1000 * 3600 * 24));
          const balance = inv.total_ttc - inv.montant_paye;
          return daysUntil >= 0 && daysUntil <= 30 && balance > 0;
        });

        setOverdueInvoices(overdue);
        setUpcomingInvoices(upcoming);
      } catch (err) {
        toast.error('Erreur lors du chargement des factures');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  /* ================= HELPERS ================= */
  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' GNF';
  };

  const getDaysDelay = (dueDate: string, overdue: boolean) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diff = Math.abs(today.getTime() - due.getTime());
    return Math.ceil(diff / (1000 * 3600 * 24));
  };

  /* ================= PAIEMENT ================= */
  const handlePay = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    const remaining = invoice.total_ttc - invoice.montant_paye;
    setPaymentAmount(remaining.toString());
    setShowPaymentModal(true);
  };

  const handleRecordPayment = async () => {
    if (!selectedInvoice) return;

    const amount = Number(paymentAmount);
    const remaining = selectedInvoice.total_ttc - selectedInvoice.montant_paye;

    if (amount <= 0 || amount > remaining) {
      toast.error('Montant invalide');
      return;
    }

    if (!paymentDate) {
      toast.error('Date de paiement obligatoire');
      return;
    }

    if (paymentMethod !== 'ESPECES' && !referenceExterne.trim()) {
      toast.error('Référence externe obligatoire');
      return;
    }

    setPaymentLoading(true);

    try {
      await createResource(`factures-client/reglement`, {
        facture_client_id: selectedInvoice.id,
         client_id: selectedInvoice.client.id,
        montant: amount,
        mode: paymentMethod,
        date_paiement: paymentDate,
        date_valeur: dateValeur || null,
        reference_externe: paymentMethod !== 'ESPECES' ? referenceExterne : null,
        notes: notes || null,
      });
      toast.success('Règlement enregistré avec succès');
      setShowPaymentModal(false);

      // Réinitialiser le formulaire
      setPaymentAmount('');
      setPaymentMethod('VIREMENT');
      setReferenceExterne('');
      setDateValeur('');
      setNotes('');

      // Recharger les données
      window.location.reload(); // ou implémente un refetch propre
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors de l’enregistrement');
    } finally {
      setPaymentLoading(false);
    }
  };

  /* ================= STATS ================= */
  const totalOverdue = overdueInvoices.reduce((sum, inv) => sum + (inv.total_ttc - inv.montant_paye), 0);
  const totalUpcoming = upcomingInvoices.reduce((sum, inv) => sum + (inv.total_ttc - inv.montant_paye), 0);

  if (loading) {
    return (
      <div className="bg-white p-8 rounded-xl shadow-sm border text-center">
        <i className="ri-loader-4-line animate-spin text-4xl text-blue-600"></i>
        <p className="mt-4 text-lg">Chargement des échéances...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">

      {/* STATS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard icon="ri-alarm-warning-line" color="red" label="En retard" count={overdueInvoices.length} amount={totalOverdue} />
        <StatCard icon="ri-calendar-todo-line" color="orange" label="À venir (30j)" count={upcomingInvoices.length} amount={totalUpcoming} />
        <StatCard icon="ri-money-dollar-circle-line" color="blue" label="Total dû" amount={totalOverdue + totalUpcoming} />
      </div>

      {/* ALERT RETARD */}
      {overdueInvoices.length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-600 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <i className="ri-error-warning-fill text-4xl text-red-600"></i>
            <div>
              <h3 className="text-xl font-bold text-red-900">Factures en retard</h3>
              <p className="text-red-800 mt-1">
                {overdueInvoices.length} facture(s) pour {formatPrice(totalOverdue)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TABLE RETARD */}
      {overdueInvoices.length > 0 && (
        <InvoiceTable title="Factures en retard" invoices={overdueInvoices} onPay={handlePay} isOverdue />
      )}

      {/* TABLE UPCOMING */}
      {upcomingInvoices.length > 0 && (
        <InvoiceTable title="Échéances à venir (30 jours)" invoices={upcomingInvoices} onPay={handlePay} />
      )}

      {/* ================= MODAL PAIEMENT ================= */}
      {showPaymentModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 max-w-2xl w-full">

            {/* Header */}
            <div className="p-6 border-b bg-gradient-to-r from-green-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <i className="ri-money-euro-circle-line text-4xl text-green-600"></i>
                  <div>
                    <h3 className="text-2xl font-bold">Enregistrer un règlement</h3>
                    <p className="text-lg text-gray-700">Facture N° {selectedInvoice.numero}</p>
                  </div>
                </div>
                <button onClick={() => setShowPaymentModal(false)} className="p-3 hover:bg-gray-100 rounded-lg">
                  <i className="ri-close-line text-2xl text-gray-600"></i>
                </button>
              </div>
            </div>

            {/* Récap */}
            <div className="p-6 bg-green-50 border-b">
              <div className="grid grid-cols-2 gap-6 text-lg">
                <div>
                  <p className="text-gray-600">Client</p>
                  <p className="font-bold">{selectedInvoice.client.raison_sociale}</p>
                </div>
                <div>
                  <p className="text-gray-600">Reste à payer</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatPrice(selectedInvoice.total_ttc - selectedInvoice.montant_paye)}
                  </p>
                </div>
              </div>
            </div>

            {/* Formulaire */}
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Montant *</label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  min="0.01"
                  max={selectedInvoice.total_ttc - selectedInvoice.montant_paye}
                  step="0.01"
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Mode de paiement *</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="VIREMENT">Virement</option>
                  <option value="CHEQUE">Chèque</option>
                  <option value="ESPECES">Espèces</option>
                  <option value="MOBILE_MONEY">Mobile Money</option>
                  <option value="CARTE">Carte bancaire</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Date du règlement *</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              {paymentMethod !== 'ESPECES' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Référence externe *</label>
                  <input
                    type="text"
                    value={referenceExterne}
                    onChange={(e) => setReferenceExterne(e.target.value)}
                    placeholder="N° chèque, ID transaction..."
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Notes</label>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            {/* Boutons (le submit est ici !) */}
            <div className="p-6 border-t bg-gray-50 flex justify-end gap-4">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-6 py-3 border rounded-lg hover:bg-gray-100 font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleRecordPayment}
                disabled={paymentLoading}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {paymentLoading && <i className="ri-loader-4-line animate-spin"></i>}
                Enregistrer le règlement
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

/* ================= SUB COMPONENTS ================= */
function StatCard({ icon, color, label, count, amount }: any) {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600">{label}</p>
          {count !== undefined && <p className="text-3xl font-bold mt-2">{count}</p>}
          <p className="text-xl font-semibold mt-1">{formatPrice(amount)}</p>
        </div>
        <i className={`${icon} text-4xl text-${color}-600`} />
      </div>
    </div>
  );
}

function InvoiceTable({ title, invoices, onPay, isOverdue = false }: any) {
  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <div className="p-6 border-b bg-gray-50">
        <h3 className="text-xl font-bold">{title} ({invoices.length})</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Facture</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">Montant dû</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Échéance</th>
              <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase">{isOverdue ? 'Retard' : 'Dans'}</th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {invoices.map((inv: Invoice) => {
              const balance = inv.total_ttc - inv.montant_paye;
              const days = getDaysDelay(inv.date_echeance, isOverdue);

              return (
                <tr key={inv.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-blue-600">{inv.numero}</div>
                    <div className="text-xs text-gray-500">BL: {inv.bon_livraison?.numero || '—'}</div>
                  </td>
                  <td className="px-6 py-4 font-medium">{inv.client.raison_sociale}</td>
                  <td className="px-6 py-4 text-right font-bold text-red-600">{formatPrice(balance)}</td>
                  <td className="px-6 py-4">{new Date(inv.date_echeance).toLocaleDateString('fr-FR')}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${isOverdue ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'}`}>
                      {days} jour{days > 1 ? 's' : ''}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => onPay(inv)}
                      className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg font-medium flex items-center gap-2"
                    >
                      <i className="ri-money-euro-circle-line"></i>
                      Payer
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const formatPrice = (amount: number) => new Intl.NumberFormat('fr-FR').format(amount) + ' GNF';
const getDaysDelay = (dueDate: string, overdue: boolean) => {
  const today = new Date();
  const due = new Date(dueDate);
  const diff = Math.abs(today.getTime() - due.getTime());
  return Math.ceil(diff / (1000 * 3600 * 24));
};